import { apiClient } from "@/lib/api/http"
import axios from "axios"

interface Envelope<T> {
  success: boolean
  statusCode: number
  message: string
  data: T
}

export interface Order {
  id: string
  quotationId: string
  buyerId: string
  status: string
  totalAmount: number
  createdAt: string
  updatedAt: string
  shipment?: {
    provider: string
    trackingUrl: string
    trackingId: string
  }
  quotation?: {
    id: string
    rfqId: string
    companyId: string
    currencyCode: string
    message: string
    status: string
    version: number
    isLatest: boolean
    createdAt: string
    updatedAt: string
  }
}

export interface OrderFilterParams {
  status?: string
  startDate?: string
  endDate?: string
  minAmount?: number
  maxAmount?: number
  buyerId?: string
  companyId?: string
  page?: number
  limit?: number
}

export async function getOrders(token: string, filters?: OrderFilterParams, signal?: AbortSignal): Promise<any> {
  const { data: envelope } = await apiClient.get<any>(
    "/orders",
    {
      headers: { Authorization: `Bearer ${token}` },
      params: filters,
      signal,
    }
  )
  
  const orders = envelope?.data || []
  const totalCount = envelope?.meta?.total ?? orders.length
  const totalPages = envelope?.meta?.totalPages ?? Math.ceil(totalCount / (filters?.limit || 10))

  Object.defineProperty(orders, "totalCount", {
    value: totalCount,
    enumerable: false,
    writable: true,
    configurable: true
  })

  Object.defineProperty(orders, "totalPages", {
    value: totalPages,
    enumerable: false,
    writable: true,
    configurable: true
  })

  Object.defineProperty(orders, "meta", {
    value: envelope?.meta,
    enumerable: false,
    writable: true,
    configurable: true
  })

  return orders
}

export async function placeOrder(quotationId: string, token: string, zipaWorldQuotationId?: string): Promise<any> {
  const { data: envelope } = await apiClient.post<Envelope<any>>(
    "/orders",
    { quotationId, zipaWorldQuotationId },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

export async function getOrderById(id: string, token: string): Promise<any> {
  const { data: envelope } = await apiClient.get<Envelope<any>>(
    `/orders/${id}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

export async function getOrderCount(token: string): Promise<number> {
  const { data: envelope } = await apiClient.get<Envelope<number>>(
    "/orders/count",
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

export async function addProductionStage(
  orderId: string,
  payload: { name: string; description: string },
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.post<Envelope<any>>(
    `/orders/${orderId}/production-stages`,
    payload,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

export async function updateProductionStageStatus(
  orderId: string,
  stageId: string,
  status: "PENDING" | "IN_PROGRESS" | "COMPLETED",
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.patch<Envelope<any>>(
    `/orders/${orderId}/production-stages/${stageId}`,
    { stageId, status },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

export async function getPayments(orderId: string, token: string): Promise<any> {
  const { data: envelope } = await apiClient.get<Envelope<any>>(
    `/orders/${orderId}/payments`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

export async function addPayment(
  orderId: string,
  payload: { type: "BUYER_TO_ADMIN" | "ADMIN_TO_SELLER"; amount: number; referenceId: string },
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.post<Envelope<any>>(
    `/orders/${orderId}/payments`,
    payload,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

export async function updatePaymentStatus(
  orderId: string,
  paymentId: string,
  status: "PENDING" | "COMPLETED" | "FAILED",
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.patch<Envelope<any>>(
    `/orders/${orderId}/payments/${paymentId}`,
    { status },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

export async function addInspection(
  orderId: string,
  message: string,
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.post<Envelope<any>>(
    "/inspection",
    { orderId, message },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

export async function updateInspectionStatus(
  inspectionId: string,
  payload: { status: string; notes?: string; documentUrl?: string },
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.patch<Envelope<any>>(
    `/inspection/${inspectionId}`,
    payload,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

export async function updateOrderStatus(
  orderId: string,
  status: string,
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.patch<Envelope<any>>(
    `/orders/${orderId}/status`,
    { status },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

export async function addShipment(
  orderId: string,
  payload: { provider: string; trackingUrl: string; trackingId: string },
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.post<Envelope<any>>(
    `/orders/${orderId}/shipment`,
    payload,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

export async function getTrackingDetails(
  url: string,
  trackingId: string
): Promise<any> {
  const { data } = await axios.post(
    url,
    { trackingNo: trackingId }
  )
  return data
}

export async function updateOrderZipaQuotation(
  orderId: string,
  zipaWorldQuotationId: string,
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.patch<Envelope<any>>(
    `/orders/${orderId}/zipa-quotation`,
    { zipaWorldQuotationId },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

export async function acceptPurchaseOrder(
  orderId: string,
  sellerInvoiceUrl: string,
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.post<Envelope<any>>(
    `/orders/${orderId}/purchase-order/accept`,
    { sellerInvoiceUrl },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

export async function rejectPurchaseOrder(
  orderId: string,
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.post<Envelope<any>>(
    `/orders/${orderId}/purchase-order/reject`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

export async function generateSellerInvoice(
  orderId: string,
  token: string
): Promise<{ invoiceUrl: string; previewUrl?: string; sellerQuotation?: any }> {
  const { data: envelope } = await apiClient.post<Envelope<{ invoiceUrl: string; previewUrl?: string; sellerQuotation?: any }>>(
    `/orders/${orderId}/seller-invoice`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

export async function getBuyerInvoiceUrl(
  orderId: string,
  token: string
): Promise<{ url: string; expiresIn: number }> {
  const { data: envelope } = await apiClient.get<Envelope<{ url: string; expiresIn: number }>>(
    `/orders/${orderId}/invoice-url`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

export async function getSellerInvoiceUrl(
  orderId: string,
  token: string
): Promise<{ url: string; expiresIn: number }> {
  const { data: envelope } = await apiClient.get<Envelope<{ url: string; expiresIn: number }>>(
    `/orders/${orderId}/seller-invoice-url`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

export async function getPurchaseOrderUrl(
  orderId: string,
  poId: string,
  token: string
): Promise<{ url: string; expiresIn: number }> {
  const { data: envelope } = await apiClient.get<Envelope<{ url: string; expiresIn: number }>>(
    `/orders/${orderId}/purchase-orders/${poId}/document-url`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

export async function getPaymentReceiptUrl(
  orderId: string,
  paymentId: string,
  token: string
): Promise<{ url: string; expiresIn: number }> {
  const { data: envelope } = await apiClient.get<Envelope<{ url: string; expiresIn: number }>>(
    `/orders/${orderId}/payments/${paymentId}/document-url`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

// ── GET /orders/buyer/status-counts ───────────────────────────────────────────
export interface OrderStatusCountsMap {
  PENDING?: number
  ACCEPTED?: number
  IN_PRODUCTION?: number
  IN_TRANSIT?: number
  COMPLETED?: number
  CANCELLED?: number
  REJECTED?: number
  [key: string]: number | undefined
}

export interface OrderStatusCountsGroup {
  total: number
  counts: OrderStatusCountsMap
}

export interface BuyerOrderStatusCountsData {
  active: OrderStatusCountsGroup
  allTime: OrderStatusCountsGroup
}

export async function getBuyerOrderStatusCounts(token: string): Promise<BuyerOrderStatusCountsData> {
  const { data: envelope } = await apiClient.get<Envelope<BuyerOrderStatusCountsData>>(
    "/orders/buyer/status-counts",
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

// ── GET /orders/fulfillment-trends ─────────────────────────────────────────────
export interface OrderFulfillmentPoint {
  date: string
  ordersFulfilled: number
  inProgress: number
}

export interface OrderFulfillmentTrendsData {
  timeframes: Record<string, OrderFulfillmentPoint[]>
}

export async function getOrderFulfillmentTrends(token: string): Promise<OrderFulfillmentTrendsData> {
  const { data: envelope } = await apiClient.get<Envelope<OrderFulfillmentTrendsData>>(
    "/orders/fulfillment-trends",
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}






export type OrderStatusCountsData = BuyerOrderStatusCountsData
export type SellerOrderStatusCountsData = BuyerOrderStatusCountsData

// ── GET /orders/seller/status-counts ───────────────────────────────────────────
export async function getSellerOrderStatusCounts(token: string): Promise<OrderStatusCountsData> {
  const { data: envelope } = await apiClient.get<Envelope<OrderStatusCountsData>>(
    "/orders/seller/status-counts",
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}
